import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useArticles } from '../../hooks/useArticles';
import { useTextSelection } from '../../hooks/useTextSelection';
import { useHighlights } from '../../hooks/useHighlights';
import { createArticleFeedback, getArticleFeedback } from '../../services/articles';
import useNavigation from '../../hooks/useNavigation';
import ShareContextMenu from '../ShareContextMenu';
import SharePreviewDialog from '../SharePreviewDialog';
import HighlightsSidebar from '../HighlightsSidebar';
import PageLoading from '../PageLoading';
import ChatPopup, { ChatFAB } from '../ChatPopup';
import { ArticleHeaderBar, ErrorBanner, ArticleMetadata, TabBar } from './ArticleHeader';
import { SummaryTab, ContentTab, OriginalTab } from './ArticleTabs';

const ArticleViewer = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [capturedSelection, setCapturedSelection] = useState({
    text: '',
    markdown: '',
    hasMarkdown: false
  });
  const [feedback, setFeedback] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [highlightsSidebarOpen, setHighlightsSidebarOpen] = useState(false);
  const [isCreatingHighlight, setIsCreatingHighlight] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { id } = useParams();
  const { goBack } = useNavigation();
  const location = useLocation();

  const {
    currentArticle,
    fetchArticle,
    removeArticle,
    regenerateArticleSummary,
    refetchArticleContent,
    markAsRead,
    markAsUnread,
    isLoading,
    isUpdating,
    isDeleting,
    isRegenerating,
    isRefetching,
    clearCurrentArticle,
    clearOperationStates,
  } = useArticles();

  const {
    selectedText,
    selectedMarkdown,
    hasMarkdown,
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    hideSelection,
  } = useTextSelection();

  const {
    highlights,
    isLoading: highlightsLoading,
    detachmentInfo,
    createHighlight,
    deleteHighlight,
    deleteAllHighlights,
    reattachHighlights,
  } = useHighlights(parseInt(id), currentArticle?.summary);

  // Fetch article on mount
  useEffect(() => {
    if (id) {
      setLocalError(null);
      clearOperationStates();

      fetchArticle(parseInt(id)).then((article) => {
        console.log('\uD83D\uDD2C [ArticleViewer] Article fetched:', {
          id: article?.id,
          hasDebugData: !!article?.debug_data,
          debugDataType: typeof article?.debug_data,
          debugDataLength: Array.isArray(article?.debug_data) ? article.debug_data.length : 'not an array',
          debugDataSample: article?.debug_data ? JSON.stringify(article.debug_data).substring(0, 200) : 'null'
        });
        fetchExistingFeedback(parseInt(id));
      }).catch(error => {
        console.error('Error fetching article:', error);
        setLocalError(error.message);
        if (error.message.includes('404')) {
          goBack();
        }
      });
    }

    return () => {
      clearCurrentArticle();
      clearOperationStates();
      setFeedback(null);
      setLocalError(null);
    };
  }, [id, fetchArticle, goBack, clearCurrentArticle, clearOperationStates]);

  // Clear local errors when switching tabs
  useEffect(() => {
    if (localError) {
      setLocalError(null);
    }
  }, [activeTab]);

  // Scroll position memory
  useEffect(() => {
    const scrollKey = `article_scroll_${id}`;
    if (location.state?.restoredFromHistory) {
      const savedScroll = sessionStorage.getItem(scrollKey);
      if (savedScroll) {
        const scrollPos = parseInt(savedScroll, 10);
        setTimeout(() => {
          window.scrollTo({ top: scrollPos, behavior: 'instant' });
        }, 100);
      }
    }

    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        sessionStorage.setItem(scrollKey, window.scrollY.toString());
      }, 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [id, location.state?.restoredFromHistory]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await removeArticle(currentArticle.id);
        goBack();
      } catch (error) {
        console.error('Error deleting article:', error);
      }
    }
  };

  const handleMarkAsRead = async () => {
    try {
      await markAsRead(currentArticle.id);
      goBack();
    } catch (error) {
      console.error('Error marking article as read:', error);
    }
  };

  const handleMarkAsReadStay = async () => {
    try {
      await markAsRead(currentArticle.id);
    } catch (error) {
      console.error('Error marking article as read:', error);
    }
  };

  const handleMarkAsUnread = async () => {
    try {
      await markAsUnread(currentArticle.id);
    } catch (error) {
      console.error('Error marking article as unread:', error);
    }
  };

  const handleRegenerateSummary = async () => {
    try {
      setLocalError(null);
      await regenerateArticleSummary(currentArticle.id);
    } catch (error) {
      console.error('Error regenerating summary:', error);
      setLocalError(error.message || 'Failed to regenerate summary');
    }
  };

  const handleRefetchContent = async () => {
    try {
      setLocalError(null);
      await refetchArticleContent(currentArticle.id);
    } catch (error) {
      console.error('Error refetching content:', error);
      setLocalError(error.message || 'Failed to refetch content');
    }
  };

  const fetchExistingFeedback = async (articleId) => {
    try {
      const feedbackData = await getArticleFeedback(articleId);
      setFeedback(feedbackData);
    } catch (error) {
      setFeedback(null);
    }
  };

  const handleFeedback = async (feedbackValue) => {
    if (feedbackLoading) return;

    setFeedbackLoading(true);
    const previousFeedback = feedback;

    setFeedback(prev => ({
      id: prev?.id || Date.now(),
      user_id: prev?.user_id || 0,
      article_id: currentArticle.id,
      feedback_value: feedbackValue,
      created_at: new Date().toISOString(),
      ...prev
    }));

    try {
      const response = await createArticleFeedback(currentArticle.id, feedbackValue);
      setFeedback(response);
    } catch (error) {
      setFeedback(previousFeedback);
      console.error('Error creating feedback:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleShareClick = () => {
    setCapturedSelection({
      text: selectedText,
      markdown: selectedMarkdown,
      hasMarkdown: hasMarkdown
    });
    setShareDialogOpen(true);
    hideSelection();
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setCapturedSelection({ text: '', markdown: '', hasMarkdown: false });
  };

  const handleCreateHighlight = async () => {
    const summaryContainer = document.querySelector('[data-markdown-source]');
    const renderedText = summaryContainer?.textContent || '';

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);
      const isWithinContainer = summaryContainer?.contains(range.commonAncestorContainer);
      if (!isWithinContainer) {
        setLocalError('Please select text from the summary to highlight');
        hideSelection();
        return;
      }
    }

    if (!selectedText || !currentArticle?.summary) return;

    try {
      setIsCreatingHighlight(true);
      await createHighlight(selectedText, null, renderedText);
      hideSelection();
    } catch (error) {
      console.error('Error creating highlight:', error);
      setLocalError(error.message || 'Failed to create highlight');
    } finally {
      setIsCreatingHighlight(false);
    }
  };

  const handleHighlightClick = (highlight) => {
    console.log('Highlight clicked:', highlight);
  };

  const handleDeleteHighlight = async (highlightId) => {
    try {
      await deleteHighlight(highlightId);
    } catch (error) {
      console.error('Error deleting highlight:', error);
      setLocalError(error.message || 'Failed to delete highlight');
    }
  };

  const handleReattachHighlights = async () => {
    try {
      await reattachHighlights(0.8);
    } catch (error) {
      console.error('Error re-attaching highlights:', error);
      setLocalError(error.message || 'Failed to re-attach highlights');
    }
  };

  const readingTime = currentArticle?.content ?
    Math.ceil(currentArticle.content.split(' ').length / 200) : 0;

  // Shared props for tab content
  const controlProps = {
    feedback,
    feedbackLoading,
    onFeedback: handleFeedback,
    isUpdating,
    onMarkAsRead: handleMarkAsRead,
    onMarkAsReadStay: handleMarkAsReadStay,
  };

  if (isLoading) {
    return (
      <PageLoading message="Loading article..." />
    );
  }

  if (localError && !currentArticle && !isRegenerating && !isRefetching) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="text-center card-clay-static p-8 max-w-md mx-4">
          <div className="text-5xl mb-4" role="img" aria-label="Error">😅</div>
          <h2 className="font-display text-xl font-bold text-heading mb-2">Oops! Can&apos;t load this article</h2>
          <p className="text-secondary-text mb-6">{localError}</p>
          <button onClick={goBack} className="btn-clay">Go Back</button>
        </div>
      </div>
    );
  }

  if (!currentArticle) return null;

  return (
    <div className="min-h-screen bg-clay-bg">
      <ArticleHeaderBar
        onGoBack={goBack}
        highlights={highlights}
        onOpenHighlights={() => setHighlightsSidebarOpen(true)}
        currentArticle={currentArticle}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        onMarkAsRead={handleMarkAsRead}
        onMarkAsUnread={handleMarkAsUnread}
        onDelete={handleDelete}
      />

      {localError && currentArticle && (
        <ErrorBanner error={localError} onDismiss={() => setLocalError(null)} />
      )}

      <ArticleMetadata article={currentArticle} readingTime={readingTime} />
      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {activeTab === 'summary' && (
          <SummaryTab
            article={currentArticle}
            articleId={parseInt(id)}
            highlights={highlights}
            onHighlightClick={handleHighlightClick}
            isRegenerating={isRegenerating}
            onRegenerateSummary={handleRegenerateSummary}
            {...controlProps}
          />
        )}
        {activeTab === 'content' && (
          <ContentTab
            article={currentArticle}
            articleId={parseInt(id)}
            isRefetching={isRefetching}
            onRefetchContent={handleRefetchContent}
            {...controlProps}
          />
        )}
        {activeTab === 'original' && (
          <OriginalTab article={currentArticle} />
        )}
      </div>

      {/* Share and Highlight Components */}
      {selectedText && (
        <ShareContextMenu
          visible={contextMenuVisible}
          position={contextMenuPosition}
          onShare={handleShareClick}
          onHighlight={handleCreateHighlight}
          onClose={hideSelection}
          hasMarkdown={hasMarkdown}
          showHighlight={activeTab === 'summary'}
          isHighlighting={isCreatingHighlight}
          selectedText={selectedText}
          ref={contextMenuRef}
        />
      )}

      <SharePreviewDialog
        isOpen={shareDialogOpen}
        onClose={handleCloseShareDialog}
        selectedText={capturedSelection.text}
        selectedMarkdown={capturedSelection.markdown}
        hasMarkdown={capturedSelection.hasMarkdown}
        articleTitle={currentArticle?.title}
        articleUrl={currentArticle?.url}
      />

      {/* Processing Toast */}
      {(isRegenerating || isRefetching) && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up-bounce">
          <div className="card-clay-sm p-4 max-w-sm">
            <div className="flex items-start gap-3">
              <div className="spinner-fun-sm w-6 h-6 flex-shrink-0"></div>
              <div className="flex-1">
                <h4 className="font-display text-sm font-bold text-heading mb-1">
                  {isRefetching ? 'Re-fetching Content...' : 'Regenerating Summary...'}
                </h4>
                <p className="text-xs text-secondary-text">
                  {isRefetching
                    ? 'This may take 1-5 minutes. Feel free to navigate away.'
                    : 'This may take 30-120 seconds. Feel free to navigate away.'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <HighlightsSidebar
        highlights={highlights}
        isOpen={highlightsSidebarOpen}
        onClose={() => setHighlightsSidebarOpen(false)}
        onHighlightClick={handleHighlightClick}
        onDeleteHighlight={handleDeleteHighlight}
        onDeleteAll={deleteAllHighlights}
        detachmentInfo={detachmentInfo}
        onReattach={handleReattachHighlights}
        isLoading={highlightsLoading}
      />

      {currentArticle && currentArticle.summary && (
        <>
          <ChatFAB
            onClick={() => setChatOpen(!chatOpen)}
            isOpen={chatOpen}
          />
          <ChatPopup
            isOpen={chatOpen}
            onClose={() => setChatOpen(false)}
            contextType="article"
            contextId={currentArticle.id}
            contextTitle={currentArticle.title || 'Article'}
          />
        </>
      )}
    </div>
  );
};

export default ArticleViewer;
